import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const WhoLikedYou = ({ user, onUpgradeClick }) => {
  const [whoLikedMe, setWhoLikedMe] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchWhoLikedMe();
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchWhoLikedMe = async () => {
    try {
      // Get all users who liked me
      const { data: likes, error: likesError } = await supabase
        .from('matches')
        .select('user_id')
        .eq('liked_user_id', user.id);

      if (likesError) throw likesError;

      if (likes && likes.length > 0) {
        const userIds = likes.map(like => like.user_id);
        
        // Get profile info for users who liked me
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        // Check which ones I haven't responded to yet
        const { data: myActions, error: actionsError } = await supabase
          .from('user_actions')
          .select('target_user_id')
          .eq('user_id', user.id)
          .in('target_user_id', userIds);

        if (actionsError) throw actionsError;

        const respondedToIds = myActions?.map(action => action.target_user_id) || [];
        
        // Filter out profiles I've already responded to
        const unrespondedProfiles = profiles?.filter(profile => 
          !respondedToIds.includes(profile.id)
        ) || [];

        setWhoLikedMe(unrespondedProfiles);
      }
    } catch (error) {
      console.error('Error fetching who liked me:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLikeBack = async (likedUserId) => {
    setActionLoading(true);
    try {
      // Record the like action
      const { error: actionError } = await supabase
        .from('user_actions')
        .insert({
          user_id: user.id,
          target_user_id: likedUserId,
          action: 'like'
        });

      if (actionError) throw actionError;

      // Insert the like into matches table
      const { error: matchError } = await supabase
        .from('matches')
        .insert({
          user_id: user.id,
          liked_user_id: likedUserId
        });

      if (matchError) throw matchError;

      alert('🎉 It\'s a match! You both liked each other!');
      
      // Refresh the list
      fetchWhoLikedMe();
    } catch (error) {
      console.error('Error liking back:', error);
      alert('Error creating match. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePass = async (passedUserId) => {
    setActionLoading(true);
    try {
      // Record the pass action
      const { error } = await supabase
        .from('user_actions')
        .insert({
          user_id: user.id,
          target_user_id: passedUserId,
          action: 'pass'
        });

      if (error) throw error;

      // Refresh the list
      fetchWhoLikedMe();
    } catch (error) {
      console.error('Error passing:', error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px',
        textAlign: 'center',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  // Show upgrade prompt for free users
  if (!subscription?.is_premium) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '25px',
          padding: '40px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>👀</div>
          <h2 style={{ 
            color: '#00E9FF', 
            marginBottom: '15px',
            fontSize: '24px'
          }}>
            See Who Liked You
          </h2>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            lineHeight: '1.6',
            marginBottom: '30px'
          }}>
            Discover who's interested in you! Upgrade to Premium to see everyone who has liked your profile.
          </p>
          
          {/* Blurred preview */}
          <div style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '30px'
          }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                filter: 'blur(8px)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }} />
            ))}
          </div>

          <button
            onClick={onUpgradeClick}
            style={{
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              boxShadow: '0 8px 32px rgba(0, 233, 255, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            💎 Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  // Premium user view
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px'
        }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Who Liked You
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px'
          }}>
            {whoLikedMe.length} {whoLikedMe.length === 1 ? 'person has' : 'people have'} liked you
          </p>
        </div>

        {whoLikedMe.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            padding: '40px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>💫</div>
            <h3 style={{ 
              color: '#00E9FF', 
              marginBottom: '15px',
              fontSize: '20px'
            }}>
              No new likes yet
            </h3>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '16px'
            }}>
              Keep swiping to get more likes!
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {whoLikedMe.map((profile) => (
              <div key={profile.id} style={{
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '20px',
                padding: '25px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                position: 'relative'
              }}>
                {/* Premium badge */}
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  ❤️ LIKED YOU
                </div>

                {/* Profile Photo */}
                <div style={{ marginBottom: '20px' }}>
                  {profile.photo_url ? (
                    <img 
                      src={profile.photo_url} 
                      alt="Profile" 
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #00E9FF',
                        boxShadow: '0 8px 25px rgba(0, 233, 255, 0.3)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      border: '3px solid #00E9FF',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '24px'
                    }}>
                      📷
                    </div>
                  )}
                </div>

                {/* Profile Info */}
                <h3 style={{ 
                  fontSize: '22px', 
                  fontWeight: '600',
                  color: '#00E9FF',
                  marginBottom: '10px'
                }}>
                  {profile.name || 'Anonymous User'}
                  {profile.age && (
                    <span style={{ 
                      fontSize: '18px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: '400'
                    }}>
                      , {profile.age}
                    </span>
                  )}
                </h3>
                
                {profile.location && (
                  <div style={{ 
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ fontSize: '14px' }}>📍</span>
                    <span style={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '14px'
                    }}>
                      {profile.location}
                    </span>
                  </div>
                )}
                
                {profile.bio && (
                  <div style={{ 
                    marginBottom: '20px',
                    padding: '15px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <p style={{ 
                      margin: 0,
                      lineHeight: '1.5', 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px'
                    }}>
                      {profile.bio}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '15px',
                  justifyContent: 'center'
                }}>
                  <button 
                    onClick={() => handlePass(profile.id)}
                    disabled={actionLoading}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: 'none',
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                      color: 'white',
                      fontSize: '24px',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      transition: 'transform 0.2s ease',
                      boxShadow: '0 8px 20px rgba(255, 107, 107, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: actionLoading ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!actionLoading) e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      if (!actionLoading) e.target.style.transform = 'scale(1)';
                    }}
                  >
                    ❌
                  </button>
                  <button 
                    onClick={() => handleLikeBack(profile.id)}
                    disabled={actionLoading}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: 'none',
                      background: 'linear-gradient(135deg, #51cf66, #40c057)',
                      color: 'white',
                      fontSize: '24px',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      transition: 'transform 0.2s ease',
                      boxShadow: '0 8px 20px rgba(81, 207, 102, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: actionLoading ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!actionLoading) e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      if (!actionLoading) e.target.style.transform = 'scale(1)';
                    }}
                  >
                    ❤️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhoLikedYou;